import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';

const defaultDataContextValue = {
    systemUser: null,
    inventory: [],
    history: [],
    suppliers: [],
    clients: [],
    onlineOrders: [],
    initialDataLoaded: false,
    settings: { bossPhone: '' },
    cart: [],
    currentClient: null
};

const globalScope = typeof globalThis !== 'undefined' ? globalThis : window;
const DATA_CONTEXT_SINGLETON_KEY = '__doutor_embalagens_data_context__';

const normalizeSystemUserAccess = (user) => {
    if (!user || typeof user !== 'object') return user;
    const username = String(user?.username || '').trim().toLowerCase();
    const name = String(user?.name || '').trim().toLowerCase();
    if (username === 'dudu' || name === 'dudu' || name === 'eduardo') {
        return {
            ...user,
            role: 'store',
            name: user?.name || 'Eduardo'
        };
    }
    return user;
};

const ensureDuduSalesSeller = (settings) => {
    if (!settings || typeof settings !== 'object') return settings;
    const salesSellers = Array.isArray(settings?.sales_sellers) ? settings.sales_sellers : [];
    const hasDudu = salesSellers.some((seller) => {
        const username = String(seller?.username || '').trim().toLowerCase();
        const id = String(seller?.id || '').trim().toLowerCase();
        const name = String(seller?.name || '').trim().toLowerCase();
        return username === 'dudu' || id === 'dudu' || name === 'eduardo' || name === 'dudu';
    });
    if (hasDudu) return settings;
    return {
        ...settings,
        sales_sellers: [
            ...salesSellers,
            { id: 'dudu', username: 'dudu', name: 'Eduardo', commissionPct: 0, active: true }
        ]
    };
};

// Mantem a mesma identidade do contexto durante o Fast Refresh do Vite.
export const DataContext = globalScope[DATA_CONTEXT_SINGLETON_KEY]
    || (globalScope[DATA_CONTEXT_SINGLETON_KEY] = createContext(defaultDataContextValue));

export function DataProvider({ children }) {
    const HISTORY_STORAGE_LIMIT = 1200;
    const HISTORY_STORAGE_FALLBACK_LIMIT = 400;
    const INITIAL_HISTORY_FETCH_LIMIT = 400;
    const POLL_HISTORY_FETCH_LIMIT = 300;
    const INITIAL_ORDERS_FETCH_LIMIT = 400;
    const POLL_ORDERS_FETCH_LIMIT = 400;
    const ORDERS_STORAGE_LIMIT = 500;
    const emptyLoadState = {
        inventory: false,
        history: false,
        suppliers: false,
        clients: false,
        orders: false,
        settings: false
    };

    const [systemUser, setSystemUser] = useState(() => {
        try {
            const stored = localStorage.getItem('doutor_system_user');
            return stored ? normalizeSystemUserAccess(JSON.parse(stored)) : null;
        } catch {
            return null;
        }
    });
    const [inventory, setInventory] = useState([]);
    const [history, setHistory] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [clients, setClients] = useState([]);
    const [onlineOrders, setOnlineOrders] = useState([]);
    const [onlineOrdersSyncError, setOnlineOrdersSyncError] = useState('');
    const [orderBoardSummary, setOrderBoardSummary] = useState(null);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);
    const [loadedResources, setLoadedResources] = useState(emptyLoadState);
    const [settings, setSettings] = useState({ bossPhone: '' });
    const [cart, setCart] = useState([]);
    const [currentClient, setCurrentClient] = useState(null);
    const loadedResourcesRef = useRef(emptyLoadState);
    const pendingResourceLoadsRef = useRef({});

    const isAuthorizedFiscalStatus = (status) => {
        const s = String(status || '').trim().toLowerCase();
        return s.includes('autoriz') || s === '100' || s.includes('conclu');
    };

    const normalizeOnlineOrderStatus = (value) => {
        const raw = String(value || '').trim().toLowerCase();
        if (!raw) return 'pending';

        if (['pending', 'novo', 'new', 'aberto'].includes(raw)) return 'pending';
        if (['approval', 'aprovacao', 'aprovação'].includes(raw)) return 'approval';
        if (['production', 'producao', 'produção'].includes(raw)) return 'production';
        if (['separating', 'separation', 'separacao', 'separação'].includes(raw)) return 'separating';
        if (['ready_for_pickup', 'ready pickup', 'pronto', 'pronto_retirada', 'pronto para retirada'].includes(raw)) return 'ready_for_pickup';
        if (['delivering', 'delivery', 'entrega', 'em entrega'].includes(raw)) return 'delivering';
        if (['conference', 'conferencia', 'conferência'].includes(raw)) return 'conference';
        if (['fiscal', 'fiscal_only', 'fiscal only'].includes(raw)) return 'fiscal_only';
        if (['budget', 'orcamento', 'orçamento'].includes(raw)) return 'budget';
        if (['cancelled', 'canceled', 'cancelado'].includes(raw)) return 'cancelled';
        if (['completed', 'delivered', 'finished', 'done', 'finalized', 'finalizado', 'concluded', 'concluido', 'concluído', 'entregue'].includes(raw)) return 'completed';

        return raw;
    };

    const resolveOnlineOrderStatus = (order) => {
        const source = order && typeof order === 'object' ? order : {};
        const rawCandidates = [
            source?.status,
            source?.orderStatus,
            source?.currentStatus,
            source?.statusLabel,
            source?.statusText,
            source?.deliveryStatus,
            source?.saleDetails?.status,
            source?.sale?.status,
            source?.trackingStatus
        ]
            .map((value) => String(value || '').trim())
            .filter(Boolean);

        const normalizedCandidates = rawCandidates
            .map((value) => normalizeOnlineOrderStatus(value))
            .filter(Boolean);

        if (normalizedCandidates.includes('cancelled')) return 'cancelled';
        if (normalizedCandidates.includes('completed')) return 'completed';
        if (normalizedCandidates.includes('budget')) return 'budget';

        const hasCompletionEvidence = [
            source?.completedAt,
            source?.finishedAt,
            source?.deliveredAt,
            source?.closedAt,
            source?.pickedUpAt,
            source?.withdrawnAt,
            source?.completedDate,
            source?.finishedDate,
            source?.deliveredDate,
            source?.closedDate
        ].some((value) => String(value || '').trim());

        if (hasCompletionEvidence) return 'completed';

        const statusPriority = [
            'fiscal_only',
            'conference',
            'delivering',
            'ready_for_pickup',
            'separating',
            'production',
            'approval',
            'pending'
        ];

        return normalizedCandidates.find((status) => statusPriority.includes(status))
            || normalizeOnlineOrderStatus(source?.status);
    };
    
    // Ref to track pending status updates to avoid polling race conditions
    const pendingOrderUpdates = useRef({});
    // Socket Ref
    const socketRef = useRef(null);

    const normalizeOrder = (order) => {
        if (!order) return null;
        
        // Tenta usar id existente ou _id do MongoDB
        let normalizedId = order.id != null ? order.id : order._id;
        
        // Se ainda não tiver ID, tenta gerar um baseado na data
        if (normalizedId == null && order.date) {
            normalizedId = new Date(order.date).getTime();
        }
        
        // Se ainda assim não tiver ID, marca como inválido (será filtrado)
        if (normalizedId == null) {
            return null;
        }

        const payments = Array.isArray(order?.payments)
            ? order.payments
                .filter(Boolean)
                .map((payment) => ({
                    ...payment,
                    amount: Number(payment?.amount || 0) || 0
                }))
            : [];

        const total = Number(order?.total || 0) || 0;
        const paidFromPayments = payments.reduce((acc, payment) => acc + (Number(payment?.amount || 0) || 0), 0);
        const explicitPaid = Number(order?.paidAmount || 0) || 0;
        const paidBase = Math.max(paidFromPayments, explicitPaid);
        const paidAmount = total > 0 ? Math.min(total, Math.max(0, paidBase)) : Math.max(0, paidBase);
        const remainingAmount = Math.max(0, total - paidAmount);
        const paymentTolerance = 0.009;
        const isFullyPaid = total > 0 && paidAmount > 0 && (paidAmount + paymentTolerance) >= total;
        const isPartiallyPaid = paidAmount > 0 && !isFullyPaid;

        const rawPaymentMethod = String(order?.paymentMethod || '').trim();
        const inferredPaymentMethod = rawPaymentMethod
            || (payments.length === 1 ? String(payments[0]?.method || '').trim() : '')
            || (order?.pix ? 'Pix' : '')
            || (order?.boleto ? 'Boleto' : '');

        const paymentDueDate = String(
            order?.paymentDueDate
            || order?.boleto?.dueDate
            || order?.pix?.dueDate
            || ''
        ).slice(0, 10) || null;

        const rawStatus = String(order?.paymentStatus || '').trim().toLowerCase();
        let paymentStatus = rawStatus || 'pending';

        if (total > 0) {
            if (isFullyPaid || rawStatus === 'paid') paymentStatus = 'paid';
            else if (isPartiallyPaid) paymentStatus = 'partial';
            else if (rawStatus === 'cancelled' || rawStatus === 'canceled') paymentStatus = 'cancelled';
            else paymentStatus = rawStatus || 'pending';
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueTime = paymentDueDate ? new Date(`${paymentDueDate}T00:00:00`).getTime() : null;
        const isOverdue = dueTime != null && dueTime < today.getTime() && remainingAmount > 0;

        const hasFinancialMethod = !!String(inferredPaymentMethod || '').trim();
        let financialStatus = 'pending';
        if (paymentStatus === 'cancelled') financialStatus = 'cancelled';
        else if (paymentStatus === 'paid') financialStatus = 'received';
        else if (paymentStatus === 'partial') financialStatus = 'partial';
        else if (remainingAmount > 0 && !hasFinancialMethod) financialStatus = 'missing_finance';
        else if (isOverdue) financialStatus = 'overdue';
        else if (remainingAmount > 0) financialStatus = 'pending';
        else financialStatus = paymentStatus || 'pending';

        const genericResponsibleNames = new Set(['loja', 'sistema', 'system', 'admin', 'online']);
        const responsibleCandidates = [
            order?.financialResponsible,
            order?.referredByName,
            order?.seller?.name,
            order?.sellerName,
            order?.referredByUsername,
            order?.seller?.username,
            order?.createdByName,
            order?.createdBy,
            order?.updatedByName,
            order?.updatedBy
        ]
            .map((value) => String(value || '').trim())
            .filter(Boolean);
        const preferredResponsible = responsibleCandidates.find((value) => !genericResponsibleNames.has(value.toLowerCase()));
        const financialResponsible = preferredResponsible || responsibleCandidates[0] || null;

        // #region debug-point payment-status-mismatch.normalizeOrder
        try {
            const debugUrl = (() => {
                try { return localStorage.getItem('trae_debug_url') || ''; } catch { return ''; }
            })() || '';
            const debugEnabled = (() => {
                try { return localStorage.getItem('trae_debug_payment_status') === '1' || localStorage.getItem('trae_debug_url'); } catch { return false; }
            })();
            const hasMismatch =
                (payments.length > 0 && Math.abs(explicitPaid - paidFromPayments) > 0.009) ||
                (paidAmount - total > 0.009) ||
                (paymentStatus === 'partial' && total > 0 && Math.abs(remainingAmount) <= 0.009);
            if (debugEnabled && hasMismatch) {
                fetch(debugUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        sessionId: 'payment-status-mismatch',
                        runId: String(localStorage.getItem('trae_debug_run') || 'pre'),
                        hypothesisId: 'H2',
                        event: 'normalize_order_mismatch',
                        ts: Date.now(),
                        data: {
                            orderId: normalizedId,
                            total,
                            explicitPaid,
                            paidFromPayments,
                            paidAmount,
                            remainingAmount,
                            paymentStatus,
                            rawPaymentStatus: rawStatus || null,
                            paymentsCount: payments.length
                        }
                    })
                }).catch(() => {});
            }
        } catch {}
        // #endregion debug-point payment-status-mismatch.normalizeOrder

        return {
            ...order,
            id: normalizedId,
            status: resolveOnlineOrderStatus(order),
            payments,
            paidAmount,
            remainingAmount,
            paymentMethod: inferredPaymentMethod || null,
            paymentStatus,
            paymentDueDate,
            financialStatus,
            financialResponsible
        };
    };

    const cacheOnlineOrder = useCallback((order) => {
        const normalized = normalizeOrder(order);
        if (!normalized) return null;
        setOnlineOrders((prev) => {
            const found = prev.some((item) => String(item.id) === String(normalized.id));
            return found
                ? prev.map((item) => String(item.id) === String(normalized.id) ? normalized : item)
                : [normalized, ...prev];
        });
        return normalized;
    }, []);

    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && 'Notification' in window) {
                if (Notification.permission !== 'granted') {
                    Notification.requestPermission().catch(() => {});
                }
            }
        } catch (e) {
            console.error('Erro ao solicitar permissão de notificação:', e);
        }

        // Socket.IO Connection for Real-time Updates
        const protocol = window.location.protocol;
        const hostname = window.location.hostname;
        const port = (hostname === 'localhost' || hostname === '127.0.0.1') ? ':8000' : ''; 
        const socketUrl = `${protocol}//${hostname}${port}`;

        socketRef.current = io(socketUrl);
        const notifyOrderChange = () => window.dispatchEvent(new Event('doutor-orders-changed'));
        socketRef.current.on('new_order', notifyOrderChange);
        socketRef.current.on('order_update', notifyOrderChange);
        socketRef.current.on('orders_updated', notifyOrderChange);

        socketRef.current.on('new_client', (newClient) => {
            console.log('Novo cliente recebido via socket:', newClient);
            setClients(prev => {
                // Avoid duplicates
                if (prev.some(c => c._id === newClient._id)) return prev;
                return [...prev, newClient];
            });
            
            // Optional: Notify user
            if (Notification.permission === 'granted') {
                new Notification('Novo Cliente Cadastrado! 👤', {
                    body: `${newClient.name} se cadastrou pelo WhatsApp.`,
                    icon: '/vite.svg'
                });
            }
        });

        return () => {
            if (socketRef.current) socketRef.current.disconnect();
        };
    }, []);

    const notifyNewOrder = (order) => {
        try {
            const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3'); // Som de sino suave
            audio.play().catch(e => console.error("Erro ao tocar som:", e));
        } catch (e) {
            console.error("Erro ao inicializar áudio:", e);
        }

        try {
            if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
                const shortId = (order.id != null ? order.id.toString() : '').slice(-6);
                const clientName = order.client?.name || 'Cliente';
                new Notification('Novo Pedido Recebido! 🛍️', {
                    body: `Pedido #${shortId} de ${clientName}\nValor: R$ ${(order.total || 0).toFixed(2)}`,
                    icon: '/vite.svg',
                    tag: `order-${order.id}`
                });
            }
        } catch (e) {
            console.error('Erro ao exibir notificação do navegador:', e);
        }
    };

    // Helper to check and notify
    const checkAndNotify = (newOrders, oldOrders) => {
        // SECURITY CHECK: Clients (Catalog Users) should NOT hear/see system notifications
        // If systemUser is null (no admin/operator/store logged in), DO NOT NOTIFY.
        if (!systemUser) return;

        const oldIds = new Set(oldOrders.map(o => o.id));
        newOrders.forEach(order => {
            if (!oldIds.has(order.id) && order.status === 'pending' && !order?.archived) {
                // If store user, skip notification for Wholesale (Franquia)
                if (systemUser?.role === 'store' && order.client?.type === 'wholesale') {
                    return;
                }

                // Check if it's really new (last 5 minutes) to avoid notifying on full reload of old pending orders
                const orderTime = new Date(order.date || Date.now()).getTime();
                if (Date.now() - orderTime < 5 * 60 * 1000) {
                     notifyNewOrder(order);
                }
            }
        });
    };

    const normalizeProduct = (product) => {
        if (!product) return null;
        let normalizedId = product.id;
        
        // If id is missing/null/undefined, try _id
        if (normalizedId == null && product._id != null) {
            normalizedId = product._id;
        }
        
        // If still missing, generate one (last resort, might cause persistence issues if not saved back)
        if (normalizedId == null) {
            normalizedId = Date.now() + Math.random(); // Add random to avoid collisions in batch
        }

        return { ...product, id: normalizedId };
    };

    const normalizeHistoryItem = (item) => ({
        ...item,
        // Preserve explicit informational events like cash closing snapshots
        direction: (item.direction != null && item.direction !== '')
            ? item.direction
            : (item.type === 'in' || item.type === 'out'
                ? item.type
                : (item.type === 'Fechamento' ? 'info' : 'out')),
        // Ensure type exists (as description)
        type: (item.type === 'in' || item.type === 'out') ? (item.reason || (item.type === 'in' ? 'Entrada' : 'Saída')) : item.type,
        // Ensure prodName exists
        prodName: item.prodName || item.productName || item.product || 'Produto desconhecido',
        // Ensure qtd exists
        qtd: item.qtd != null ? item.qtd : (item.quantity != null ? item.quantity : 0),
        // Ensure value exists (optional)
        value: (item.value ?? item.totalValue ?? item.total ?? item.saleDetails?.totalValue ?? item.saleDetails?.total ?? 0)
    });

    const compactHistoryItemForStorage = (item) => {
        if (!item || typeof item !== 'object') return item;
        const saleDetails = item?.saleDetails && typeof item.saleDetails === 'object'
            ? {
                id: item.saleDetails.id,
                _id: item.saleDetails._id,
                date: item.saleDetails.date,
                realSaleDate: item.saleDetails.realSaleDate,
                client: item.saleDetails.client ? {
                    name: item.saleDetails.client.name,
                    phone: item.saleDetails.client.phone,
                    document: item.saleDetails.client.document,
                    cpf: item.saleDetails.client.cpf,
                    cnpj: item.saleDetails.client.cnpj,
                    type: item.saleDetails.client.type
                } : undefined,
                customer: item.saleDetails.customer ? {
                    name: item.saleDetails.customer.name,
                    phone: item.saleDetails.customer.phone,
                    document: item.saleDetails.customer.document,
                    cpf: item.saleDetails.customer.cpf,
                    cnpj: item.saleDetails.customer.cnpj,
                    type: item.saleDetails.customer.type
                } : undefined,
                clientName: item.saleDetails.clientName,
                clientPhone: item.saleDetails.clientPhone,
                sellerName: item.saleDetails.sellerName,
                seller: item.saleDetails.seller ? {
                    id: item.saleDetails.seller.id,
                    name: item.saleDetails.seller.name,
                    username: item.saleDetails.seller.username
                } : undefined,
                total: item.saleDetails.total,
                subtotal: item.saleDetails.subtotal,
                shippingCost: item.saleDetails.shippingCost,
                discount: item.saleDetails.discount,
                paymentMethod: item.saleDetails.paymentMethod,
                paymentDueDate: item.saleDetails.paymentDueDate,
                status: item.saleDetails.status,
                channel: item.saleDetails.channel,
                createdBy: item.saleDetails.createdBy,
                createdByName: item.saleDetails.createdByName,
                createdByUsername: item.saleDetails.createdByUsername,
                orderId: item.saleDetails.orderId,
                originalOrderId: item.saleDetails.originalOrderId,
                followUpOnlineOrderId: item.saleDetails.followUpOnlineOrderId,
                items: Array.isArray(item.saleDetails.items)
                    ? item.saleDetails.items.slice(0, 40).map((historyItem) => ({
                        id: historyItem?.id,
                        _id: historyItem?._id,
                        productId: historyItem?.productId,
                        name: historyItem?.name,
                        productName: historyItem?.productName,
                        quantity: historyItem?.quantity,
                        qty: historyItem?.qty,
                        price: historyItem?.price,
                        total: historyItem?.total,
                        unitType: historyItem?.unitType,
                        stockOrigin: historyItem?.stockOrigin,
                        type: historyItem?.type
                    }))
                    : undefined
            }
            : undefined;

        return {
            id: item.id,
            _id: item._id,
            orderId: item.orderId,
            date: item.date,
            timestamp: item.timestamp,
            type: item.type,
            prodName: item.prodName,
            productName: item.productName,
            quantity: item.quantity,
            qtd: item.qtd,
            value: item.value,
            direction: item.direction,
            paymentMethod: item.paymentMethod,
            user: item.user,
            reason: item.reason,
            deleted: item.deleted,
            saleDetails
        };
    };

    const buildHistoryStorageSnapshot = (items, limit) => {
        const list = Array.isArray(items) ? items : [];
        return list.slice(0, Math.max(0, limit)).map(compactHistoryItemForStorage);
    };

    const compactOrderForStorage = (order) => {
        if (!order || typeof order !== 'object') return order;
        return {
            id: order.id,
            _id: order._id,
            code: order.code,
            orderCode: order.orderCode,
            sequentialId: order.sequentialId,
            clientTxnId: order.clientTxnId,
            date: order.date,
            createdAt: order.createdAt,
            updatedAt: order.updatedAt,
            status: order.status,
            orderStatus: order.orderStatus,
            paymentStatus: order.paymentStatus,
            paymentMethod: order.paymentMethod,
            paymentDueDate: order.paymentDueDate,
            total: order.total,
            paidAmount: order.paidAmount,
            remainingAmount: order.remainingAmount,
            financialStatus: order.financialStatus,
            financialResponsible: order.financialResponsible,
            seller: order.seller ? {
                id: order.seller.id,
                name: order.seller.name,
                username: order.seller.username
            } : undefined,
            sellerName: order.sellerName,
            referredByName: order.referredByName,
            referredByUsername: order.referredByUsername,
            createdBy: order.createdBy,
            createdByName: order.createdByName,
            createdByUsername: order.createdByUsername,
            clientId: order.clientId,
            clientName: order.clientName,
            clientPhone: order.clientPhone,
            client: order.client ? {
                _id: order.client._id,
                id: order.client.id,
                name: order.client.name,
                phone: order.client.phone,
                type: order.client.type
            } : undefined,
            customer: order.customer ? {
                _id: order.customer._id,
                id: order.customer.id,
                name: order.customer.name,
                phone: order.customer.phone,
                type: order.customer.type
            } : undefined,
            payments: Array.isArray(order.payments)
                ? order.payments.slice(0, 12).map((payment) => ({
                    id: payment?.id,
                    _id: payment?._id,
                    amount: payment?.amount,
                    value: payment?.value,
                    method: payment?.method,
                    type: payment?.type,
                    date: payment?.date,
                    createdAt: payment?.createdAt,
                    paidAt: payment?.paidAt,
                    user: payment?.user,
                    note: payment?.note
                }))
                : undefined,
            items: Array.isArray(order.items)
                ? order.items.slice(0, 60).map((item) => ({
                    id: item?.id,
                    _id: item?._id,
                    productId: item?.productId,
                    name: item?.name,
                    productName: item?.productName,
                    prodName: item?.prodName,
                    quantity: item?.quantity,
                    qty: item?.qty,
                    qtd: item?.qtd,
                    unitPrice: item?.unitPrice,
                    price: item?.price,
                    salePrice: item?.salePrice,
                    total: item?.total,
                    totalPrice: item?.totalPrice,
                    totalValue: item?.totalValue,
                    unitType: item?.unitType,
                    unit: item?.unit,
                    category: item?.category,
                    boxQty: item?.boxQty
                }))
                : undefined,
            pix: order.pix ? {
                status: order.pix.status,
                dueDate: order.pix.dueDate,
                receiptUrl: order.pix.receiptUrl
            } : undefined,
            boleto: order.boleto ? {
                status: order.boleto.status,
                dueDate: order.boleto.dueDate
            } : undefined
        };
    };

    const buildOrdersStorageSnapshot = (items, limit) => {
        const list = Array.isArray(items) ? items : [];
        return list.slice(0, Math.max(0, limit)).map(compactOrderForStorage);
    };

    const mergeRecentHistoryItems = (currentItems, recentItems) => {
        const currentList = Array.isArray(currentItems) ? currentItems : [];
        const recentList = Array.isArray(recentItems) ? recentItems : [];
        if (recentList.length === 0) return currentList;

        const map = new Map();
        for (const item of currentList) {
            const key = String(item?._id || item?.id || item?.timestamp || '');
            if (key) map.set(key, item);
        }
        for (const item of recentList) {
            const key = String(item?._id || item?.id || item?.timestamp || '');
            if (key) map.set(key, item);
        }

        return Array.from(map.values()).sort((a, b) => {
            const tb = Number(b?.timestamp || new Date(b?.date || 0).getTime() || 0);
            const ta = Number(a?.timestamp || new Date(a?.date || 0).getTime() || 0);
            return tb - ta;
        });
    };

    const normalizeSupplier = (s) => {
        if (!s) return null;
        if (typeof s === 'string') {
            const name = String(s || '').trim();
            if (!name) return null;
            return { _id: null, name };
        }
        const name = String(s?.name || '').trim();
        if (!name) return null;
        return { ...s, name };
    };

    const normalizeSuppliersList = (list) => {
        const arr = Array.isArray(list) ? list : [];
        const out = [];
        const seen = new Set();
        for (const s of arr) {
            const ns = normalizeSupplier(s);
            if (!ns) continue;
            const key = String(ns?._id || '').trim() || ns.name.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(ns);
        }
        out.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'pt-BR'));
        return out;
    };

    const setLoadedResourcesSafe = useCallback((updater) => {
        setLoadedResources((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            loadedResourcesRef.current = next;
            return next;
        });
    }, []);

    const markResourcesLoaded = useCallback((resources) => {
        const list = Array.isArray(resources) ? resources : [resources];
        setLoadedResourcesSafe((prev) => {
            let changed = false;
            const next = { ...prev };
            list.forEach((resource) => {
                if (!next[resource]) {
                    next[resource] = true;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [setLoadedResourcesSafe]);

    const getStoredJson = useCallback((key, fallback) => {
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return fallback;
            if (key === 'doutor_inventory' && raw.length > 750_000) {
                try { localStorage.removeItem(key); } catch {}
                return fallback;
            }
            return JSON.parse(raw);
        } catch {
            return fallback;
        }
    }, []);

    const queueResourceLoad = useCallback((resource, loader) => {
        if (loadedResourcesRef.current[resource]) {
            return Promise.resolve(true);
        }
        if (pendingResourceLoadsRef.current[resource]) {
            return pendingResourceLoadsRef.current[resource];
        }

        const task = (async () => {
            await loader();
            markResourcesLoaded(resource);
            return true;
        })().finally(() => {
            delete pendingResourceLoadsRef.current[resource];
        });

        pendingResourceLoadsRef.current[resource] = task;
        return task;
    }, [markResourcesLoaded]);

    const ensureSettingsLoaded = useCallback(() => queueResourceLoad('settings', async () => {
        const storedSettings = ensureDuduSalesSeller(getStoredJson('doutor_settings', { bossPhone: '' }));
        if (storedSettings && Object.keys(storedSettings).length > 0) {
            setSettings((prev) => ({ ...prev, ...storedSettings }));
        }

        try {
            const res = await fetch('/api/settings');
            if (!res.ok) return;
            const serverSettings = ensureDuduSalesSeller(await res.json().catch(() => null));
            if (serverSettings && Object.keys(serverSettings).length > 0) {
                setSettings((prev) => ({ ...prev, ...serverSettings }));
            }
        } catch {}
    }), [getStoredJson, queueResourceLoad]);

    const ensureInventoryLoaded = useCallback(() => queueResourceLoad('inventory', async () => {
        const storedInventoryRaw = getStoredJson('doutor_inventory', []);
        const storedInventory = Array.isArray(storedInventoryRaw)
            ? storedInventoryRaw.map(({ image, ...rest }) => rest)
            : [];

        if (inventory.length === 0 && storedInventory.length > 0) {
            setInventory(storedInventory.map(normalizeProduct));
        }

        try {
            const res = await fetch('/api/inventory?light=true');
            if (!res.ok) return;
            const serverInventory = await res.json().catch(() => []);
            setInventory(Array.isArray(serverInventory) ? serverInventory.map(normalizeProduct) : []);
        } catch {}
    }), [getStoredJson, inventory.length, queueResourceLoad]);

    const ensureHistoryLoaded = useCallback(() => queueResourceLoad('history', async () => {
        const storedHistory = getStoredJson('doutor_history', []);
        if (history.length === 0 && Array.isArray(storedHistory) && storedHistory.length > 0) {
            setHistory(storedHistory.map(normalizeHistoryItem));
        }

        try {
            const res = await fetch(`/api/history?limit=${INITIAL_HISTORY_FETCH_LIMIT}`);
            if (!res.ok) return;
            const serverHistory = await res.json().catch(() => []);
            setHistory(Array.isArray(serverHistory) ? serverHistory.map(normalizeHistoryItem) : []);
        } catch {}
    }), [getStoredJson, history.length, queueResourceLoad, INITIAL_HISTORY_FETCH_LIMIT]);

    const ensureClientsLoaded = useCallback(() => queueResourceLoad('clients', async () => {
        const storedClients = getStoredJson('doutor_clients', []);
        if (clients.length === 0 && Array.isArray(storedClients) && storedClients.length > 0) {
            setClients(storedClients);
        }

        try {
            const res = await fetch('/api/clients');
            if (!res.ok) return;
            const serverClients = await res.json().catch(() => []);
            setClients(Array.isArray(serverClients) ? serverClients : []);
        } catch {}
    }), [clients.length, getStoredJson, queueResourceLoad]);

    const ensureSuppliersLoaded = useCallback(() => queueResourceLoad('suppliers', async () => {
        const storedSuppliers = getStoredJson('doutor_suppliers', []);
        if (suppliers.length === 0 && Array.isArray(storedSuppliers) && storedSuppliers.length > 0) {
            setSuppliers(normalizeSuppliersList(storedSuppliers));
        }

        try {
            const res = await fetch('/api/suppliers');
            if (!res.ok) return;
            const serverSuppliers = await res.json().catch(() => []);
            setSuppliers(normalizeSuppliersList(serverSuppliers));
        } catch {}
    }), [getStoredJson, queueResourceLoad, suppliers.length]);

    const ensureOrdersLoaded = useCallback(() => queueResourceLoad('orders', async () => {
        const storedOnlineOrders = getStoredJson('doutor_online_orders', []);
        if (onlineOrders.length === 0 && Array.isArray(storedOnlineOrders) && storedOnlineOrders.length > 0) {
            setOnlineOrders(
                storedOnlineOrders
                    .map(normalizeOrder)
                    .filter(o => o !== null)
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
            );
        }

        try {
            const initialOrdersUrl = INITIAL_ORDERS_FETCH_LIMIT > 0
                ? `/api/orders?limit=${INITIAL_ORDERS_FETCH_LIMIT}`
                : '/api/orders';
            const res = await fetch(initialOrdersUrl);
            if (!res.ok) {
                setOnlineOrdersSyncError(`Falha ao carregar pedidos do servidor (${res.status}).`);
                return;
            }
            const serverOrders = await res.json().catch(() => []);
            if (Array.isArray(serverOrders)) {
                const normalizedServer = serverOrders
                    .map(normalizeOrder)
                    .filter(o => o !== null)
                    .sort((a, b) => new Date(b.date) - new Date(a.date));
                setOnlineOrders(normalizedServer);
                setOnlineOrdersSyncError('');
            } else {
                setOnlineOrdersSyncError('Pedidos nao carregados corretamente do servidor.');
            }
        } catch {
            setOnlineOrdersSyncError('Falha ao carregar pedidos do servidor. Verifique a conexao/servidor.');
        }
    }), [getStoredJson, onlineOrders.length, queueResourceLoad, INITIAL_ORDERS_FETCH_LIMIT]);

    const resolveRouteResources = useCallback((pathname) => {
        const path = String(pathname || '/').trim().toLowerCase();
        const resources = new Set(['settings']);

        if (path.startsWith('/catalogo')) {
            resources.add('inventory');
            return Array.from(resources);
        }

        if (path.startsWith('/perfil')) {
            return Array.from(resources);
        }

        if ([
            '/vendas', '/estoque', '/gestao', '/historico', '/historico-franquia', '/pedidos',
            '/relatorios-junior', '/relatorios-franquia', '/recebimentos', '/financeiro',
            '/financeiro-vendedor', '/interior', '/fast-order', '/estoque-controle',
            '/config', '/fechamento'
        ].includes(path)) {
            resources.add('inventory');
        }

        if ([
            '/historico', '/historico-franquia', '/gestao', '/recebimentos', '/financeiro',
            '/financeiro-vendedor', '/vendedores', '/fechamento', '/estoque', '/interior',
            '/relatorios-junior', '/relatorios-franquia', '/estoque-controle', '/config',
            '/operador'
        ].includes(path)) {
            resources.add('history');
        }

        if ([
            '/clientes', '/franqueados', '/vendas', '/historico', '/historico-franquia',
            '/pedidos', '/recebimentos', '/financeiro', '/financeiro-vendedor', '/minhas-vendas',
            '/cobrancas', '/cobranças', '/cobrancas-vendedor', '/meus-inadimplentes',
            '/repasse', '/franquias', '/config'
        ].includes(path)) {
            resources.add('clients');
        }

        if (['/fornecedores', '/estoque', '/config', '/estoque-controle'].includes(path)) {
            resources.add('suppliers');
        }

        if ([
            '/gestao', '/recebimentos', '/financeiro', '/financeiro-vendedor',
            '/minhas-vendas', '/cobrancas', '/cobranças', '/cobrancas-vendedor',
            '/meus-inadimplentes', '/repasse', '/franquias',
            '/chat', '/fechamento', '/operador', '/fabrica', '/vendedores', '/historico',
            '/historico-franquia'
        ].includes(path)) {
            resources.add('orders');
        }

        return Array.from(resources);
    }, []);

    const ensureRouteData = useCallback(async (pathname) => {
        const resources = resolveRouteResources(pathname);
        const pendingResources = resources.filter((resource) => !loadedResourcesRef.current[resource]);

        if (pendingResources.length === 0) {
            setInitialDataLoaded(true);
            return true;
        }

        setInitialDataLoaded(false);

        const loaders = {
            settings: ensureSettingsLoaded,
            inventory: ensureInventoryLoaded,
            history: ensureHistoryLoaded,
            clients: ensureClientsLoaded,
            suppliers: ensureSuppliersLoaded,
            orders: ensureOrdersLoaded
        };

        await Promise.all(pendingResources.map((resource) => loaders[resource]?.() || Promise.resolve(true)));
        setInitialDataLoaded(true);
        return true;
    }, [
        ensureClientsLoaded,
        ensureHistoryLoaded,
        ensureInventoryLoaded,
        ensureOrdersLoaded,
        ensureSettingsLoaded,
        ensureSuppliersLoaded,
        resolveRouteResources
    ]);

    // Load data from Server (with localStorage fallback and sync)
    useEffect(() => {
        const loadData = async () => {
            const storedSettings = ensureDuduSalesSeller(getStoredJson('doutor_settings', { bossPhone: '' }));
            const storedCart = getStoredJson('doutor_cart', []);
            const storedCatalogUser = getStoredJson('doutor_catalog_user', null);

            setSettings(storedSettings);
            setCart(storedCart);
            if (storedCatalogUser) setCurrentClient(storedCatalogUser);

            try {
                const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
                const isCatalogOnlyMode = !!storedCatalogUser && !systemUser;
                if (isCatalogOnlyMode) {
                    await ensureRouteData(currentPath || '/catalogo');
                } else {
                    await ensureRouteData(currentPath);
                }
            } catch (error) {
                console.error("Erro ao carregar dados iniciais:", error);
            } finally {
                setInitialDataLoaded(true);
            }
        };

        loadData();
    }, [systemUser]);

    useEffect(() => {
        if (!systemUser || !loadedResources.orders) return;

        let isMounted = true;
        let timeoutId = null;
        let nextDelayMs = 15000;

        const pollOrders = async () => {
            if (!isMounted) return;

            try {
                if (window.location.pathname === '/pedidos' || document.visibilityState === 'hidden') {
                    nextDelayMs = 30000;
                    return;
                }
                const res = await fetch(`/api/orders?limit=${POLL_ORDERS_FETCH_LIMIT}`);
                if (res.ok && isMounted) {
                    const serverOrders = await res.json();
                    const normalized = serverOrders
                        .map(normalizeOrder)
                        .filter(o => o !== null);
                    
                    setOnlineOrders(prev => {
                        const newOrdersMap = new Map(normalized.map(o => [String(o.clientTxnId || o.id), o]));
                        
                        let mergedList = prev.map(prevOrder => {
                            const prevKey = String(prevOrder.clientTxnId || prevOrder.id);
                            if (newOrdersMap.has(prevKey)) {
                                const newOrder = newOrdersMap.get(prevKey);
                                newOrdersMap.delete(prevKey);
                                
                                const pendingStatus = pendingOrderUpdates.current[newOrder.id];
                                if (pendingStatus) {
                                    if (newOrder.status === pendingStatus) {
                                        delete pendingOrderUpdates.current[newOrder.id];
                                        return newOrder;
                                    } else {
                                        return { ...newOrder, status: pendingStatus };
                                    }
                                }
                                return newOrder;
                            }
                            return prevOrder;
                        });

                        const brandNewOrders = Array.from(newOrdersMap.values()).map(newOrder => {
                             const pendingStatus = pendingOrderUpdates.current[newOrder.id];
                             if (pendingStatus) {
                                if (newOrder.status === pendingStatus) {
                                    delete pendingOrderUpdates.current[newOrder.id];
                                    return newOrder;
                                } else {
                                    return { ...newOrder, status: pendingStatus };
                                }
                             }
                             return newOrder;
                        });

                        mergedList = [...brandNewOrders, ...mergedList];

                        checkAndNotify(normalized, prev);

                        const uniqMap = new Map();
                        for (const o of mergedList) {
                            const key = String(o.clientTxnId || o.id);
                            uniqMap.set(key, o);
                        }
                        const uniqList = Array.from(uniqMap.values());
                        const finalSorted = uniqList.sort((a, b) => new Date(b.date) - new Date(a.date));
                        const same =
                            Array.isArray(prev) &&
                            prev.length === finalSorted.length &&
                            prev.every((po, idx) => {
                                const no = finalSorted[idx];
                                if (!no) return false;
                                const pk = String(po?.clientTxnId || po?.id || '');
                                const nk = String(no?.clientTxnId || no?.id || '');
                                if (pk !== nk) return false;
                                if (String(po?.status || '') !== String(no?.status || '')) return false;
                                if (String(po?.paymentStatus || '') !== String(no?.paymentStatus || '')) return false;
                                if (Number(po?.paidAmount || 0) !== Number(no?.paidAmount || 0)) return false;
                                if (Number(po?.remainingAmount || 0) !== Number(no?.remainingAmount || 0)) return false;
                                if (String(po?.coraStatus || '') !== String(no?.coraStatus || '')) return false;
                                if (String(po?.pix?.status || '') !== String(no?.pix?.status || '')) return false;
                                if (String(po?.pix?.receiptUrl || '') !== String(no?.pix?.receiptUrl || '')) return false;
                                if (String(po?.boleto?.status || '') !== String(no?.boleto?.status || '')) return false;
                                if (Number(po?.total || 0) !== Number(no?.total || 0)) return false;
                                const pil = Array.isArray(po?.items) ? po.items.length : 0;
                                const nil = Array.isArray(no?.items) ? no.items.length : 0;
                                if (pil !== nil) return false;
                                return true;
                            });

                        return same ? prev : finalSorted;
                    });
                    nextDelayMs = 15000;
                    setOnlineOrdersSyncError('');
                } else {
                    nextDelayMs = Math.min(180000, Math.max(30000, nextDelayMs * 2));
                    setOnlineOrdersSyncError(`Falha ao atualizar pedidos do servidor (${res.status}).`);
                }
            } catch (error) {
                const errorName = String(error?.name || '').trim();
                const errorMessage = String(error?.message || '').trim().toLowerCase();
                const isTransientFetchFailure = errorName === 'TypeError' && errorMessage.includes('failed to fetch');
                if (!isTransientFetchFailure) {
                    console.error('Erro no polling de pedidos:', error);
                }
                nextDelayMs = Math.min(180000, Math.max(30000, nextDelayMs * 2));
                setOnlineOrdersSyncError('Falha ao atualizar pedidos do servidor.');
            } finally {
                if (isMounted) {
                    timeoutId = setTimeout(pollOrders, nextDelayMs);
                }
            }
        };

        timeoutId = setTimeout(pollOrders, 8000);

        return () => {
            isMounted = false;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [loadedResources.orders, systemUser]);

    useEffect(() => {
        if (!systemUser || !loadedResources.history) return;

        let isMounted = true;
        let timeoutId = null;
        let nextDelayMs = 30000;

        const pollHistory = async () => {
            if (!isMounted) return;
            if (window.location.pathname === '/pedidos' || (typeof document !== 'undefined' && document.visibilityState !== 'visible')) {
                timeoutId = setTimeout(pollHistory, nextDelayMs);
                return;
            }

            try {
                const res = await fetch(`/api/history?limit=${POLL_HISTORY_FETCH_LIMIT}`);
                if (res.ok && isMounted) {
                    const serverHistory = await res.json();
                    const normalized = Array.isArray(serverHistory) ? serverHistory.map(normalizeHistoryItem) : [];

                    setHistory((prev) => {
                        const merged = mergeRecentHistoryItems(prev, normalized);
                        const same =
                            Array.isArray(prev) &&
                            prev.length === merged.length &&
                            prev.every((item, idx) => {
                                const nextItem = merged[idx];
                                if (!nextItem) return false;
                                const prevKey = String(item?._id || item?.id || item?.timestamp || '');
                                const nextKey = String(nextItem?._id || nextItem?.id || nextItem?.timestamp || '');
                                if (prevKey !== nextKey) return false;
                                if (Number(item?.timestamp || 0) !== Number(nextItem?.timestamp || 0)) return false;
                                if (String(item?.type || '') !== String(nextItem?.type || '')) return false;
                                if (Number(item?.value || 0) !== Number(nextItem?.value || 0)) return false;
                                return true;
                            });
                        return same ? prev : merged;
                    });

                    nextDelayMs = 30000;
                } else {
                    nextDelayMs = Math.min(180000, Math.max(45000, nextDelayMs * 2));
                }
            } catch (error) {
                console.error('Erro no polling de historico:', error);
                nextDelayMs = Math.min(180000, Math.max(45000, nextDelayMs * 2));
            } finally {
                if (isMounted) {
                    timeoutId = setTimeout(pollHistory, nextDelayMs);
                }
            }
        };

        timeoutId = setTimeout(pollHistory, 12000);

        return () => {
            isMounted = false;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [loadedResources.history, systemUser]);

    // Save data to localStorage whenever it changes
    useEffect(() => {
        if (inventory.length > 0) {
            try {
                const inventoryNoImages = inventory.map(({ image, ...rest }) => rest);
                localStorage.setItem('doutor_inventory', JSON.stringify(inventoryNoImages));
            } catch (e) {
                console.error('Error saving inventory to localStorage:', e);
            }
        }
    }, [inventory]);

    useEffect(() => {
        if (history.length > 0) {
            try {
                localStorage.setItem('doutor_history', JSON.stringify(buildHistoryStorageSnapshot(history, HISTORY_STORAGE_LIMIT)));
            } catch (e) {
                try {
                    localStorage.setItem('doutor_history', JSON.stringify(buildHistoryStorageSnapshot(history, HISTORY_STORAGE_FALLBACK_LIMIT)));
                } catch (fallbackError) {
                    console.error('Error saving history to localStorage:', fallbackError);
                }
            }
        } else {
            try {
                localStorage.removeItem('doutor_history');
            } catch {}
        }
    }, [history]);

    useEffect(() => {
        try {
            localStorage.setItem('doutor_suppliers', JSON.stringify(suppliers));
        } catch (e) {
            console.error('Error saving suppliers to localStorage:', e);
        }
    }, [suppliers]);

    useEffect(() => {
        if (clients.length > 0) {
            try {
                localStorage.setItem('doutor_clients', JSON.stringify(clients));
            } catch (e) {
                console.error('Error saving clients to localStorage:', e);
            }
        }
    }, [clients]);

    useEffect(() => {
        try {
            localStorage.setItem('doutor_settings', JSON.stringify(settings));
        } catch (e) {
            console.error('Error saving settings to localStorage:', e);
        }
    }, [settings]);

    useEffect(() => {
        try {
            localStorage.setItem('doutor_cart', JSON.stringify(cart));
        } catch (e) {
            console.error('Error saving cart to localStorage:', e);
        }
    }, [cart]);

    useEffect(() => {
        try {
            localStorage.setItem('doutor_online_orders', JSON.stringify(buildOrdersStorageSnapshot(onlineOrders, ORDERS_STORAGE_LIMIT)));
        } catch (e) {
            console.error('Error saving online_orders to localStorage:', e);
        }
    }, [onlineOrders]);

    // Sync across tabs
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'doutor_online_orders') {
                // `onlineOrders` already syncs with the backend via polling and optimistic updates.
                // Ignoring cross-tab localStorage updates here avoids stale snapshots from another tab
                // forcing the same order to bounce back and forth between statuses.
                return;
            }
            if (e.key === 'doutor_inventory') {
                const newInventory = JSON.parse(e.newValue || '[]');
                setInventory(Array.isArray(newInventory) ? newInventory.map(normalizeProduct) : []);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Actions
    const addProduct = async (product) => {
        // Calculate next ID between 1 and 999
        const usedIds = new Set(inventory.map(p => parseInt(p.id)).filter(id => !isNaN(id)));
        let nextId = 1;
        while (usedIds.has(nextId)) {
            nextId++;
        }

        const newProduct = { ...product, id: nextId };
        setInventory(prev => [...prev, newProduct]);
        try {
            await fetch('/api/inventory', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newProduct)
            });
        } catch (e) { console.error(e); }
    };

    const updateProduct = async (id, updatedFields) => {
        setInventory(prev => prev.map(p => {
            if (p.id === id) {
                const newProduct = { ...p, ...updatedFields };
                // Prevent negative stock manually
                if (newProduct.quantityRetail < 0) newProduct.quantityRetail = 0;
                if (newProduct.quantityWholesale < 0) newProduct.quantityWholesale = 0;
                return newProduct;
            }
            return p;
        }));
        try {
            const res = await fetch(`/api/inventory/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedFields)
            });
            if (!res.ok) {
                throw new Error('Falha ao salvar no servidor');
            }
        } catch (e) { 
            console.error('Erro detalhado ao salvar:', e);
            // Check if it's a size issue (unlikely with 50mb limit but good to know)
            if (e.message.includes('Payload Too Large')) {
                 alert('ERRO: A imagem ou dados são muito grandes para salvar.');
            } else {
                 alert(`ERRO: Falha ao salvar alteração! Verifique sua conexão. (${e.message})`);
            }
            // Revert local change if needed? For now just alert.
        }
    };

    const deleteProduct = async (id) => {
        const productToDelete = inventory.find(p => p.id === id);
        setInventory(prev => prev.filter(p => p.id !== id));
        try {
            const res = await fetch(`/api/inventory/${id}`, { method: 'DELETE' });
            if (!res.ok) {
                // If 404, it means it's already gone from server, so we are good (don't restore).
                if (res.status === 404) return;
                throw new Error('Failed to delete');
            }
        } catch (e) { 
            console.error(e);
            if (productToDelete) {
                setInventory(prev => [...prev, productToDelete]);
            }
            alert('Erro ao excluir produto. Tente novamente.');
        }
    };

    const addHistory = async (type, prodName, qtd, value, direction, paymentMethod = null, saleDetails = null, supplier = null) => {
        const createdBy = systemUser?.username || systemUser?.name || 'Sistema';
        const normalizedSaleDetails = saleDetails && typeof saleDetails === 'object'
            ? {
                ...saleDetails,
                createdBy,
                origin: saleDetails.origin || (createdBy === 'pedro' ? 'santoriny' : 'store'),
                channel: saleDetails.channel || (createdBy === 'pedro' ? 'santoriny' : undefined)
            }
            : saleDetails;
        const newItem = {
            id: Date.now(),
            date: new Date().toLocaleString('pt-BR'),
            timestamp: Date.now(),
            type,
            prodName,
            qtd,
            value,
            direction,
            paymentMethod,
            saleDetails: normalizedSaleDetails,
            user: createdBy,
            supplier
        };
        setHistory(prev => [newItem, ...prev]);
        try {
            await fetch('/api/history', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newItem)
            });
        } catch (e) { console.error(e); }
    };

    const updateHistoryItem = (id, updatedData) => {
        setHistory(prev => prev.map(item => item.id === id ? { ...item, ...updatedData } : item));
    };

    const restoreBackup = (data) => {
        if (data.inventory && Array.isArray(data.inventory)) setInventory(data.inventory);
        if (data.history && Array.isArray(data.history)) setHistory(data.history);
        if (data.suppliers && Array.isArray(data.suppliers)) setSuppliers(data.suppliers);
        if (data.clients && Array.isArray(data.clients)) setClients(data.clients);
    };

    const ONLINE_ORDER_HISTORY_VISIBLE_STATUSES = new Set(['ready_for_pickup', 'delivering', 'completed', 'delivered']);

    const getHistoryOrderKey = (entry) => String(
        entry?.orderId ??
        entry?.saleDetails?.originalOrderId ??
        entry?.saleDetails?.id ??
        entry?.id ??
        ''
    );

    const shouldShowOnlineOrderInHistory = (order, hasExistingEntry = false) => {
        if (!order || order.id == null) return false;
        if (order.skipOnlineHistory || order.historyRegisteredAtCheckout) return false;
        const status = String(order.status || '').toLowerCase();
        if (status === 'cancelled' || status === 'budget') return false;
        if (ONLINE_ORDER_HISTORY_VISIBLE_STATUSES.has(status)) {
            const isDirectWhatsappCompletion =
                status === 'completed' &&
                String(order.channel || '').toLowerCase() === 'whatsapp' &&
                !order.stockReserved;

            if (isDirectWhatsappCompletion && !hasExistingEntry) {
                return false;
            }
            return true;
        }
        return false;
    };

    const buildOnlineOrderHistoryItem = (order) => {
        const safeOrder = order || {};
        const totalQty = (safeOrder.items || []).reduce((acc, i) => acc + (i.qty || i.quantity || 0), 0);
        return {
            id: safeOrder.id,
            type: 'Venda Online',
            prodName: `Pedido Online #${safeOrder.id} - ${safeOrder.client?.name || 'Cliente'} (${safeOrder.items?.length || 0} itens)`,
            productName: `Pedido Online #${safeOrder.id} - ${safeOrder.client?.name || 'Cliente'} (${safeOrder.items?.length || 0} itens)`,
            quantity: totalQty,
            qtd: totalQty,
            value: safeOrder.total || 0,
            date: safeOrder.date,
            timestamp: safeOrder.id,
            user: safeOrder.updatedBy || safeOrder.createdBy || 'Online',
            reason: 'Venda Online',
            orderId: safeOrder.id,
            direction: 'out',
            saleDetails: safeOrder
        };
    };

    const removeOnlineOrderHistoryLocal = (orderId, previousOrderId = null) => {
        if (orderId == null && previousOrderId == null) return;
        const nextOrderKey = orderId != null ? String(orderId) : null;
        const prevOrderKey = previousOrderId != null ? String(previousOrderId) : null;

        setHistory(prev => prev.filter(h => {
            const hOrderKey = getHistoryOrderKey(h);
            if (nextOrderKey && hOrderKey === nextOrderKey) return false;
            if (prevOrderKey && hOrderKey === prevOrderKey) return false;
            return true;
        }));
    };

    const upsertOnlineOrderHistoryLocal = (order, previousOrderId = null) => {
        if (!order || order.id == null) return;
        if (order.skipOnlineHistory || order.historyRegisteredAtCheckout) return;

        setHistory(prev => {
            const prevOrderKey = previousOrderId != null ? String(previousOrderId) : null;
            const nextOrderKey = String(order.id);
            let found = false;
            const next = prev.map(h => {
                const hOrderKey = getHistoryOrderKey(h);
                if (hOrderKey === nextOrderKey || (prevOrderKey && hOrderKey === prevOrderKey)) {
                    found = true;
                    if (!shouldShowOnlineOrderInHistory(order, true)) {
                        return null;
                    }
                    const nextHistoryItem = buildOnlineOrderHistoryItem(order);
                    return { ...h, ...nextHistoryItem };
                }
                return h;
            }).filter(Boolean);

            if (found) {
                return next;
            }

            if (!shouldShowOnlineOrderInHistory(order, false)) {
                return next;
            }

            return [buildOnlineOrderHistoryItem(order), ...next];
        });
    };

    const addSale = async (sale, updateStock = true) => {
        const createdBy = sale?.createdBy || systemUser?.username || systemUser?.name || 'Sistema';
        const saleWithMeta = { ...(sale || {}), createdBy };
        const computedSaleTotal = (() => {
            const direct = Number(saleWithMeta?.total ?? saleWithMeta?.totalValue ?? saleWithMeta?.value);
            if (Number.isFinite(direct) && direct > 0) return direct;
            const directSubtotal = Number(saleWithMeta?.subtotal);
            const directShipping = Number(saleWithMeta?.shippingCost ?? saleWithMeta?.freight ?? 0) || 0;
            const directDiscount = Number(saleWithMeta?.discount ?? saleWithMeta?.commercialDiscountValue ?? 0) || 0;
            if (Number.isFinite(directSubtotal) && directSubtotal > 0) {
                return Math.max(0, directSubtotal + directShipping - directDiscount);
            }
            return (Array.isArray(saleWithMeta?.items) ? saleWithMeta.items : []).reduce((acc, item) => {
                const qty = Number(item?.quantity ?? item?.qty ?? 0) || 0;
                const totalLine = Number(item?.total ?? item?.totalPrice ?? item?.totalValue ?? item?.lineTotal);
                if (Number.isFinite(totalLine) && totalLine > 0) return acc + totalLine;
                const unit = Number(item?.price ?? item?.unitPrice ?? item?.salePrice ?? item?.value ?? 0) || 0;
                return acc + (unit * qty);
            }, 0);
        })();
        const historyItem = {
            id: saleWithMeta.id,
            orderId: saleWithMeta.originalOrderId ?? saleWithMeta.orderId ?? undefined,
            date: saleWithMeta.date,
            timestamp: saleWithMeta.id,
            type: saleWithMeta.saleType || 'Venda',
            prodName: `${(saleWithMeta.items || []).length} itens`,
            productName: `${(saleWithMeta.items || []).length} itens`,
            qtd: (saleWithMeta.items || []).reduce((acc, i) => acc + (i.quantity || 0), 0),
            quantity: (saleWithMeta.items || []).reduce((acc, i) => acc + (i.quantity || 0), 0),
            value: computedSaleTotal,
            totalValue: computedSaleTotal,
            direction: 'out',
            paymentMethod: saleWithMeta.paymentMethod,
            saleDetails: { ...saleWithMeta, total: computedSaleTotal, totalValue: computedSaleTotal, value: computedSaleTotal },
            user: createdBy,
            supplier: null
        };

        setHistory(prev => [historyItem, ...prev]);
        
        // Update inventory (Local)
        if (updateStock) {
            setInventory(prev => prev.map(p => {
                const item = (saleWithMeta.items || []).find(i => {
                    const itemId = i.productId || i.originalId || i.id;
                    return String(itemId) === String(p.id);
                });
                if (item) {
                    const origin = String(item.stockOrigin || 'retail').toLowerCase();
                    if (origin !== 'wholesale') {
                        const newQty = Math.max(0, p.quantityRetail - item.quantity);
                        return { ...p, quantityRetail: newQty };
                    } else {
                        const newQty = Math.max(0, p.quantityWholesale - item.quantity);
                        return { ...p, quantityWholesale: newQty };
                    }
                }
                return p;
            }));
        }

        // Server Atomic Update
        const itemsToUpdate = (saleWithMeta.items || []).map(item => ({
            id: item.productId || item.originalId || item.id,
            quantityToReduce: item.quantity,
            stockOrigin: item.stockOrigin || 'retail'
        }));

        try {
            await fetch('/api/sales', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ historyItem, itemsToUpdate, skipStockUpdate: !updateStock })
            });
        } catch (e) { console.error(e); }
    };

    const addInteriorWithdrawal = async ({ sellerId, sellerName, items }) => {
        const createdBy = systemUser?.username || 'Sistema';
        const normalizedItems = Array.isArray(items) ? items.map(i => {
            const productId = i.id != null ? i.id : i._id;
            const quantity = Number(i.quantity || i.qty || 0) || 0;
            const unitType = i.unitType || 'un';

            const product = inventory.find(p => {
                const pid = p.id != null ? p.id : p._id;
                return String(pid) === String(productId);
            });
            const costAtSale = Number(product?.costPrice || 0);

            return {
                id: productId,
                productId,
                name: i.name,
                quantity,
                unitType,
                costAtSale
            };
        }).filter(i => i.id != null && i.quantity > 0) : [];

        if (!sellerId || !sellerName) {
            alert('Selecione um vendedor do interior.');
            return null;
        }
        if (normalizedItems.length === 0) {
            alert('Carrinho vazio.');
            return null;
        }

        const totalQty = normalizedItems.reduce((acc, i) => acc + i.quantity, 0);
        const historyItem = {
            id: Date.now(),
            date: new Date().toLocaleString('pt-BR'),
            timestamp: Date.now(),
            type: 'Retirada Interior',
            prodName: `${sellerName} (${normalizedItems.length} itens)`,
            productName: `${sellerName} (${normalizedItems.length} itens)`,
            qtd: totalQty,
            quantity: totalQty,
            value: 0,
            direction: 'out',
            paymentMethod: null,
            saleDetails: {
                interior: true,
                sellerId,
                sellerName,
                items: normalizedItems,
                createdBy
            },
            supplier: null
        };

        setHistory(prev => [historyItem, ...prev]);

        setInventory(prev => prev.map(p => {
            const item = normalizedItems.find(i => {
                const pId = p.id != null ? p.id : p._id;
                return i.id == pId;
            });
            if (item) {
                const newQty = Math.max(0, (p.quantityRetail || 0) - item.quantity);
                return { ...p, quantityRetail: newQty };
            }
            return p;
        }));

        try {
            const res = await fetch('/api/interior/withdrawals', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sellerId, sellerName, items: normalizedItems, createdBy })
            });
            const data = await res.json();
            if (!res.ok) {
                alert(data.error || 'Erro ao registrar retirada.');
                return null;
            }
            return data;
        } catch (e) {
            console.error(e);
            alert('Erro ao registrar retirada.');
            return null;
        }
    };

    const addSupplier = async (supplierInput) => {
        const supplier = normalizeSupplier(supplierInput);
        if (!supplier) return null;

        const nameKey = String(supplier.name || '').trim().toLowerCase();
        if (!nameKey) return null;

        const exists = suppliers.some(s => String(s?.name || '').trim().toLowerCase() === nameKey);
        if (exists) return null;

        const optimistic = { ...supplier, _id: supplier._id || `local_${Date.now()}` };
        setSuppliers(prev => normalizeSuppliersList([...prev, optimistic]));

        try {
            const res = await fetch('/api/suppliers', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(supplier)
            });
            const data = await res.json().catch(() => null);
            if (!res.ok) {
                setSuppliers(prev => prev.filter(s => String(s?._id || '') !== String(optimistic._id)));
                alert(data?.error || 'Erro ao salvar fornecedor.');
                return null;
            }
            setSuppliers(prev => normalizeSuppliersList(prev.map(s => String(s?._id || '') === String(optimistic._id) ? data : s)));
            return data;
        } catch (e) {
            console.error(e);
            setSuppliers(prev => prev.filter(s => String(s?._id || '') !== String(optimistic._id)));
            alert('Erro ao salvar fornecedor.');
            return null;
        }
    };

    const updateSupplier = async (id, patch) => {
        const key = String(id || '').trim();
        if (!key) return null;
        const prev = suppliers;
        setSuppliers(s => normalizeSuppliersList(s.map(it => String(it?._id || '') === key ? { ...it, ...(patch || {}) } : it)));
        try {
            const res = await fetch(`/api/suppliers/${encodeURIComponent(key)}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(patch || {})
            });
            const data = await res.json().catch(() => null);
            if (!res.ok) {
                setSuppliers(prev);
                alert(data?.error || 'Erro ao atualizar fornecedor.');
                return null;
            }
            setSuppliers(s => normalizeSuppliersList(s.map(it => String(it?._id || '') === key ? data : it)));
            return data;
        } catch (e) {
            console.error(e);
            setSuppliers(prev);
            alert('Erro ao atualizar fornecedor.');
            return null;
        }
    };

    const deleteSupplier = async (id) => {
        const key = String(id || '').trim();
        if (!key) return;
        const prev = suppliers;
        setSuppliers(s => s.filter(it => String(it?._id || '') !== key));
        try {
            const res = await fetch(`/api/suppliers/${encodeURIComponent(key)}`, { method: 'DELETE' });
            const data = await res.json().catch(() => null);
            if (!res.ok) {
                setSuppliers(prev);
                alert(data?.error || 'Erro ao excluir fornecedor.');
            }
        } catch (e) {
            console.error(e);
            setSuppliers(prev);
            alert('Erro ao excluir fornecedor.');
        }
    };

    const addClient = async (client) => {
        const digitsOnly = (v) => String(v || '').replace(/\D/g, '');
        const docKey = (c) => digitsOnly(c?.document || c?.cpf || c?.cnpj);
        const phoneKey = (c) => digitsOnly(c?.phone);
        const clientPayload = { ...client };

        try {
            const res = await fetch('/api/clients', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(clientPayload)
            });
            const saved = await res.json().catch(() => null);
            if (!res.ok) {
                alert(saved?.error || 'Erro ao salvar cliente.');
                return null;
            }

            if (saved) {
                setClients(prev => {
                    const list = Array.isArray(prev) ? prev : [];
                    const d = docKey(saved);
                    const p = phoneKey(saved);
                    const idx = list.findIndex(c => {
                        if (saved._id && String(c?._id || '') === String(saved._id)) return true;
                        if (d && docKey(c) === d) return true;
                        if (p && phoneKey(c) === p) return true;
                        return false;
                    });
                    if (idx === -1) return [...list, saved];
                    const next = list.slice();
                    next[idx] = { ...next[idx], ...saved };
                    return next;
                });
            }
            return saved;
        } catch (e) {
            console.error(e);
            alert('Erro ao salvar cliente.');
            return null;
        }
    };

    const deleteClient = async (id) => {
        const key = String(id || '').trim();
        if (!key) return;
        const prevClients = clients;
        setClients(prev => prev.filter(c => {
            const cid = String(c._id || c.id || c.phone || '').trim();
            return cid !== key && String(c.phone || '').trim() !== key;
        }));
        try {
            const res = await fetch(`/api/clients/${encodeURIComponent(key)}`, { method: 'DELETE' });
            if (!res.ok) {
                const data = await res.json().catch(() => null);
                alert(data?.error || 'Erro ao excluir cliente.');
                setClients(prevClients);
            }
        } catch (e) {
            console.error(e);
            setClients(prevClients);
            alert('Erro ao excluir cliente.');
        }
    };

    const addOnlineOrder = async (order) => {
        const existingClientTxnId = String(order?.clientTxnId || '').trim();
        const generatedClientTxnId =
            (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const clientTxnId = existingClientTxnId || generatedClientTxnId;

        const newOrder = normalizeOrder({
            id: Date.now(),
            date: new Date().toISOString(),
            status: 'pending', // pending, completed, cancelled
            stockReserved: typeof order?.stockReserved === 'boolean'
                ? order.stockReserved
                : order.channel === 'whatsapp' ? false : true,
            ...order,
            clientTxnId: String(clientTxnId)
        });
        
        const previousInventory = inventory;
        const shouldReserveInventoryOptimistically = !newOrder.skipStockUpdate && newOrder.channel !== 'whatsapp' && order.items && Array.isArray(order.items);

        // Optimistic update (Orders)
        setOnlineOrders(prev => [newOrder, ...prev]);

        removeOnlineOrderHistoryLocal(newOrder.id);

        // Optimistic update (Inventory)
        if (shouldReserveInventoryOptimistically) {
            setInventory(prev => prev.map(p => {
                // Robust ID matching (handles string/number and id/_id mix)
                const item = order.items.find(i => {
                    const iId = i.id != null ? i.id : i._id;
                    const pId = p.id != null ? p.id : p._id;
                    return iId == pId; // Loose equality for string/number match
                });

                if (item) {
                    const qty = item.qty || item.quantity || 0;
                    const origin = String(item.stockOrigin || 'retail').toLowerCase();
                    const isRetail = origin !== 'wholesale';
                    
                    if (isRetail) {
                        const newQty = Math.max(0, (p.quantityRetail || 0) - qty);
                        return { ...p, quantityRetail: newQty };
                    } else {
                        const newQty = Math.max(0, (p.quantityWholesale || 0) - qty);
                        return { ...p, quantityWholesale: newQty };
                    }
                }
                return p;
            }));
        }

        // Save to server
        try {
            const res = await fetch('/api/orders', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newOrder)
            });
            const data = await res.json().catch(() => null);

            if (res.ok) {
                const normalizedSavedOrder = normalizeOrder({
                    ...(data && typeof data === 'object' ? data : {}),
                    id: data?.id ?? data?._id ?? data?.orderId ?? newOrder.id,
                    clientTxnId: String(data?.clientTxnId || newOrder.clientTxnId || '')
                }) || normalizeOrder(newOrder) || newOrder;
                
                // Update ID in OnlineOrders (replace temp ID with real Sequential ID)
                setOnlineOrders(prev => {
                    // Substitui o temp-id pelo savedOrder
                    const replaced = prev.map(o => (o.id === newOrder.id ? normalizedSavedOrder : o));
                    // Remove duplicados usando chave estável (clientTxnId se existir, senão id)
                    const seen = new Set();
                    const deduped = [];
                    for (const o of replaced) {
                        const key = String(o.clientTxnId || o.id);
                        if (!seen.has(key)) {
                            seen.add(key);
                            deduped.push(o);
                        }
                    }
                    return deduped;
                });
                
                upsertOnlineOrderHistoryLocal(normalizedSavedOrder, newOrder.id);
                
                return normalizedSavedOrder;
            }
            
            setOnlineOrders(prev => prev.filter(o => o.id !== newOrder.id));
            if (shouldReserveInventoryOptimistically) {
                setInventory(previousInventory);
            }
            alert(data?.error || 'Pedido recusado pelo servidor.');
            return null;
        } catch (error) {
            console.error('Erro ao salvar pedido no servidor', error);
            setOnlineOrders(prev => prev.filter(o => o.id !== newOrder.id));
            if (shouldReserveInventoryOptimistically) {
                setInventory(previousInventory);
            }
            alert('Erro ao salvar pedido no servidor.');
            return null;
        }
    };

    const updateOnlineOrderStatus = async (id, status) => {
        console.log(`[updateOnlineOrderStatus] Updating Order ${id} to ${status}`);
        if (id == null) {
            console.error('Tentativa de atualizar status de pedido sem ID');
            return null;
        }

        const orderIdKey = String(id);
        const updatedByUsername = String(systemUser?.username || '').trim();
        const updatedByName = String(systemUser?.name || systemUser?.username || '').trim() || 'Sistema';
        const updatedBy = updatedByUsername || updatedByName || 'Sistema';
        const nowIso = new Date().toISOString();
        const previousInventory = inventory;

        // Optimistic Update (Order Status)
        // Mark as pending update to protect against polling race conditions
        pendingOrderUpdates.current[orderIdKey] = status;

        let previousOrderSnapshot = null;
        let nextOrderSnapshot = null;
        setOnlineOrders(prev => prev.map(o => {
            if (String(o?.id ?? '') === orderIdKey) {
                previousOrderSnapshot = o;
                // #region debug-point C:status-requested
                // #endregion
                // Handle cancellation optimistic stock restoration
                if (status === 'cancelled' && o.stockReserved) {
                    // We need to restore stock in local inventory
                    setInventory(inv => inv.map(p => {
                        const item = o.items.find(i => i.id === p.id);
                        if (item) {
                             const qty = item.qty || item.quantity || 0;
                             const origin = String(item.stockOrigin || 'retail').toLowerCase();
                             if (origin !== 'wholesale') {
                                 return { ...p, quantityRetail: (p.quantityRetail || 0) + qty };
                             } else {
                                 return { ...p, quantityWholesale: (p.quantityWholesale || 0) + qty };
                             }
                        }
                        return p;
                    }));
                    nextOrderSnapshot = normalizeOrder({ 
                        ...o,
                        status,
                        stockReserved: false,
                        updatedBy,
                        updatedByName,
                        updatedByUsername: updatedByUsername || undefined,
                        updatedAt: nowIso
                    });
                    return nextOrderSnapshot;
                }
                nextOrderSnapshot = normalizeOrder({
                    ...o,
                    status,
                    updatedBy,
                    updatedByName,
                    updatedByUsername: updatedByUsername || undefined,
                    updatedAt: nowIso
                });
                return nextOrderSnapshot;
            }
            return o;
        }));

        if (nextOrderSnapshot) {
            upsertOnlineOrderHistoryLocal(nextOrderSnapshot);
        }

        try {
            const res = await fetch(`/api/orders/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    status,
                    updatedBy,
                    updatedByName,
                    updatedByUsername: updatedByUsername || undefined,
                    updatedAt: nowIso
                })
            });
            const data = await res.json().catch(() => null);

            if (!res.ok) {
                throw new Error(data?.error || 'Erro ao atualizar status do pedido.');
            }

            const normalized = normalizeOrder(data);
            // #region debug-point B:status-response
            // #endregion
            if (normalized) {
                setOnlineOrders(prev => prev.map(o => String(o?.id ?? '') === orderIdKey ? normalized : o));
                upsertOnlineOrderHistoryLocal(normalized);
            }

            delete pendingOrderUpdates.current[orderIdKey];
            return normalized;
        } catch (error) {
            console.error('Erro ao atualizar status no servidor', error);
            if (previousOrderSnapshot) {
                setOnlineOrders(prev => prev.map(o => String(o?.id ?? '') === orderIdKey ? previousOrderSnapshot : o));
                upsertOnlineOrderHistoryLocal(previousOrderSnapshot);
            }
            if (status === 'cancelled') {
                setInventory(previousInventory);
            }
            delete pendingOrderUpdates.current[orderIdKey];
            alert(error?.message || 'Erro ao atualizar status do pedido.');
            return null;
        }
    };

    const updateOnlineOrder = async (id, updates) => {
        if (id == null) {
            console.error('Tentativa de atualizar pedido sem ID');
            return null;
        }

        const orderIdKey = String(id);
        const updatedByUsername = String(systemUser?.username || '').trim();
        const updatedByName = String(systemUser?.name || systemUser?.username || '').trim() || 'Sistema';
        const updatedBy = updatedByUsername || updatedByName || 'Sistema';
        const nowIso = new Date().toISOString();

        const nextUpdatesBase = {
            ...(updates || {}),
            updatedBy,
            updatedByName,
            updatedByUsername: updatedByUsername || undefined,
            updatedAt: nowIso
        };

        if (updates?.status) {
             pendingOrderUpdates.current[orderIdKey] = updates.status;
        }

        const matchesOnlineOrderId = (order) => {
            const identifiers = [
                order?.id,
                order?._id,
                order?.orderCode,
                order?.sequentialId,
                order?.originalOrderId
            ];
            return identifiers.some((value) => String(value ?? '').trim() === orderIdKey);
        };

        let previousOrderSnapshot = null;
        let nextOrderSnapshot = null;
        setOnlineOrders(prev => {
            const existing = prev.find(matchesOnlineOrderId) || null;
            previousOrderSnapshot = existing;
            const existingHasDate = !!(existing?.date);
            const updates = { ...nextUpdatesBase };
            if (existingHasDate) {
                delete updates.date;
            }
            return prev.map(o => {
                if (matchesOnlineOrderId(o)) {
                    const hasExplicitDate = !!(updates.date && String(updates.date).trim());
                    const preservedDate = !hasExplicitDate && o?.date ? o.date : undefined;
                    const merged = { ...o, ...updates };
                    if (preservedDate) merged.date = preservedDate;
                    nextOrderSnapshot = normalizeOrder(merged);
                    return nextOrderSnapshot;
                }
                return o;
            });
        });

        const nextUpdates = { ...nextUpdatesBase };
        const hasExistingDate =
            (previousOrderSnapshot && !!previousOrderSnapshot.date) ||
            (() => {
                try {
                    const found = onlineOrders.find(matchesOnlineOrderId);
                    return !!(found?.date);
                } catch { return false; }
            })();
        if (hasExistingDate) {
            delete nextUpdates.date;
        }

        // #region debug-point payment-status-mismatch.updateOnlineOrder
        try {
            const debugUrl = (() => {
                try { return localStorage.getItem('trae_debug_url') || ''; } catch { return ''; }
            })();
            const debugEnabled = (() => {
                try { return (localStorage.getItem('trae_debug_payment_status') === '1' || localStorage.getItem('trae_debug_url')) && !!debugUrl; } catch { return false; }
            })();
            if (debugEnabled && (updates?.items || updates?.total != null || updates?.paidAmount != null || updates?.paymentStatus || updates?.payments)) {
                fetch(debugUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        sessionId: 'payment-status-mismatch',
                        runId: String(localStorage.getItem('trae_debug_run') || 'pre'),
                        hypothesisId: 'H3',
                        event: 'update_online_order_snapshot',
                        ts: Date.now(),
                        data: {
                            orderId: id,
                            previousTotal: Number(previousOrderSnapshot?.total || 0) || 0,
                            previousPaidAmount: Number(previousOrderSnapshot?.paidAmount || 0) || 0,
                            previousPaymentStatus: String(previousOrderSnapshot?.paymentStatus || ''),
                            previousPaymentsCount: Array.isArray(previousOrderSnapshot?.payments) ? previousOrderSnapshot.payments.length : 0,
                            nextTotal: Number(nextOrderSnapshot?.total || 0) || 0,
                            nextPaidAmount: Number(nextOrderSnapshot?.paidAmount || 0) || 0,
                            nextRemainingAmount: Number(nextOrderSnapshot?.remainingAmount || 0) || 0,
                            nextPaymentStatus: String(nextOrderSnapshot?.paymentStatus || ''),
                            nextPaymentsCount: Array.isArray(nextOrderSnapshot?.payments) ? nextOrderSnapshot.payments.length : 0,
                            updatesTotal: updates?.total ?? null,
                            updatesPaymentStatus: updates?.paymentStatus ?? null
                        }
                    })
                }).catch(() => {});
            }
        } catch {}
        // #endregion

        if (nextOrderSnapshot) {
            upsertOnlineOrderHistoryLocal(nextOrderSnapshot);
        }

        try {
            const res = await fetch(`/api/orders/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(nextUpdates)
            });
            const data = await res.json().catch(() => null);

            if (!res.ok) {
                throw new Error(data?.error || 'Erro ao atualizar pedido.');
            }

            const normalized = normalizeOrder(data);
            // #region debug-point payment-status-mismatch.updateOnlineOrder
            try {
                const debugUrl = (() => {
                    try { return localStorage.getItem('trae_debug_url') || ''; } catch { return ''; }
                })() || '';
                const debugEnabled = (() => {
                    try { return localStorage.getItem('trae_debug_payment_status') === '1' || localStorage.getItem('trae_debug_url'); } catch { return false; }
                })();
                if (debugEnabled && normalized) {
                    fetch(debugUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            sessionId: 'payment-status-mismatch',
                            runId: String(localStorage.getItem('trae_debug_run') || 'pre'),
                            hypothesisId: 'H4',
                            event: 'update_online_order_response',
                            ts: Date.now(),
                            data: {
                                orderId: id,
                                total: Number(normalized?.total || 0) || 0,
                                paidAmount: Number(normalized?.paidAmount || 0) || 0,
                                remainingAmount: Number(normalized?.remainingAmount || 0) || 0,
                                paymentStatus: String(normalized?.paymentStatus || ''),
                                paymentsCount: Array.isArray(normalized?.payments) ? normalized.payments.length : 0
                            }
                        })
                    }).catch(() => {});
                }
            } catch {}
            // #endregion
            if (normalized) {
                setOnlineOrders(prev => prev.map(o => String(o?.id ?? '') === orderIdKey ? normalized : o));
                upsertOnlineOrderHistoryLocal(normalized);
            }

            if (updates.status) {
                delete pendingOrderUpdates.current[orderIdKey];
            }
            return normalized;
        } catch (error) {
            console.error('Erro ao atualizar pedido no servidor', error);
            if (previousOrderSnapshot) {
                setOnlineOrders(prev => prev.map(o => String(o?.id ?? '') === orderIdKey ? previousOrderSnapshot : o));
                upsertOnlineOrderHistoryLocal(previousOrderSnapshot);
            }
            if (updates.status) {
                delete pendingOrderUpdates.current[orderIdKey];
            }
            alert(error?.message || 'Erro ao atualizar pedido.');
            return null;
        }
    };

    const addOnlineOrderPayment = async (orderId, paymentInput) => {
        if (orderId == null) return;
        const amount = (() => {
            const raw = paymentInput?.amount;
            if (typeof raw === 'number') return Number(raw) || 0;
            const s = String(raw == null ? '' : raw).trim();
            if (!s) return 0;
            const cleaned = s.replace(/[^\d,.\-]/g, '');
            if (!cleaned) return 0;
            if (cleaned.includes(',') && cleaned.includes('.')) {
                const normalized = cleaned.replace(/\./g, '').replace(',', '.');
                return Number(normalized) || 0;
            }
            if (cleaned.includes(',')) {
                const normalized = cleaned.replace(',', '.');
                return Number(normalized) || 0;
            }
            return Number(cleaned) || 0;
        })();
        if (!amount || amount <= 0) {
            alert('Valor inválido.');
            return null;
        }

        // #region debug-point register-payment-missing.client.addOnlineOrderPayment
        const reportRegisterPaymentDebugEvent = async (payload) => {
            try {
                const url = (() => {
                    try { return localStorage.getItem('trae_debug_url') || ''; } catch { return ''; }
                })() || '';
                const enabled = (() => {
                    try { return localStorage.getItem('trae_debug') === '1' || localStorage.getItem('trae_debug_url'); } catch { return false; }
                })();
                if (!enabled) return;
                await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        sessionId: 'register-payment-missing',
                        runId: String(localStorage.getItem('trae_debug_run') || 'pre'),
                        hypothesisId: String(payload?.hypothesisId || 'H?'),
                        ts: Date.now(),
                        event: payload?.event || 'register_payment',
                        data: payload?.data || {}
                    })
                });
            } catch {}
        };
        await reportRegisterPaymentDebugEvent({
            hypothesisId: 'H1',
            event: 'register_payment_start',
            data: {
                orderId,
                amount,
                method: paymentInput?.method,
                user: systemUser?.username || 'Sistema'
            }
        });
        // #endregion debug-point register-payment-missing.client.addOnlineOrderPayment

        const payment = {
            id: Date.now(),
            date: new Date().toISOString(),
            amount,
            method: paymentInput?.method || 'Dinheiro',
            note: paymentInput?.note || null,
            user: systemUser?.username || 'Sistema',
            ...(paymentInput?.receiptFile ? {
                receipt: {
                    fileName: paymentInput.receiptFile.name,
                    fileType: paymentInput.receiptFile.type,
                    size: Number(paymentInput.receiptFile.size || 0) || 0
                }
            } : {})
        };

        const historyItem = {
            id: payment.id,
            type: 'Recebimento',
            prodName: `Pagamento Pedido Online #${orderId} - ${paymentInput?.clientName || ''}`.trim(),
            productName: `Pagamento Pedido Online #${orderId} - ${paymentInput?.clientName || ''}`.trim(),
            quantity: 0,
            qtd: 0,
            value: amount,
            date: payment.date,
            timestamp: payment.id,
            user: payment.user,
            reason: 'Pagamento',
            orderId: orderId,
            direction: 'in',
            paymentMethod: payment.method,
            saleDetails: { orderId: orderId, payment }
        };

        try {
            const hasReceiptFile = paymentInput?.receiptFile instanceof File;
            const requestInit = hasReceiptFile
                ? (() => {
                    const form = new FormData();
                    form.append('amount', String(amount));
                    form.append('method', String(payment.method || 'Dinheiro'));
                    if (payment.note) form.append('note', String(payment.note));
                    form.append('user', String(payment.user || 'Sistema'));
                    form.append('receipt', paymentInput.receiptFile);
                    return { method: 'POST', body: form };
                })()
                : {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ amount, method: payment.method, note: payment.note, user: payment.user })
                };
            const res = await fetch(`/api/orders/${orderId}/payments`, requestInit);
            const data = await res.json();
            if (!res.ok) {
                alert(data.error || 'Erro ao registrar pagamento.');
                // #region debug-point register-payment-missing.client.addOnlineOrderPayment
                await reportRegisterPaymentDebugEvent({
                    hypothesisId: 'H2',
                    event: 'register_payment_api_error',
                    data: {
                        orderId,
                        httpStatus: res.status,
                        error: data?.error || null,
                        responseKeys: data && typeof data === 'object' ? Object.keys(data) : null
                    }
                });
                // #endregion debug-point register-payment-missing.client.addOnlineOrderPayment
                return null;
            }
            // Use the persisted payment, including the permanent receipt URL.
            const savedPayment = Array.isArray(data?.payments) ? data.payments[data.payments.length - 1] : null;
            const savedHistoryItem = savedPayment ? {
                ...historyItem,
                id: savedPayment.id ?? historyItem.id,
                timestamp: savedPayment.id ?? historyItem.timestamp,
                date: savedPayment.date || historyItem.date,
                value: Number(savedPayment.amount ?? historyItem.value),
                saleDetails: { ...historyItem.saleDetails, payment: savedPayment }
            } : historyItem;
            let normalizedPaidOrder = normalizeOrder(data);
            // #region debug-point register-payment-missing.client.addOnlineOrderPayment
            await reportRegisterPaymentDebugEvent({
                hypothesisId: 'H2',
                event: 'register_payment_api_ok',
                data: {
                    orderId,
                    httpStatus: res.status,
                    paymentStatus: normalizedPaidOrder?.paymentStatus,
                    paidAmount: normalizedPaidOrder?.paidAmount,
                    remainingAmount: normalizedPaidOrder?.remainingAmount,
                    paymentsCount: Array.isArray(normalizedPaidOrder?.payments) ? normalizedPaidOrder.payments.length : null
                }
            });
            // #endregion debug-point register-payment-missing.client.addOnlineOrderPayment
            try {
                const status = String(normalizedPaidOrder?.paymentStatus || '').trim().toLowerCase();
                const hasCora =
                    !!String(normalizedPaidOrder?.coraInvoiceId || '').trim() ||
                    (Array.isArray(normalizedPaidOrder?.charges) && normalizedPaidOrder.charges.some((c) => {
                        const inv = String(c?.invoiceId || c?.coraInvoiceId || '').trim();
                        const st = String(c?.status || '').trim().toLowerCase();
                        return !!inv && !['paid', 'cancelled', 'canceled'].includes(st);
                    }));
                if (status === 'paid' && hasCora) {
                    const cancelRes = await fetch(`/api/orders/${orderId}/cora/cancel-open`, { method: 'POST' });
                    if (cancelRes.ok) {
                        const cancelData = await cancelRes.json();
                        if (cancelData?.order) {
                            normalizedPaidOrder = normalizeOrder(cancelData.order) || normalizedPaidOrder;
                        }
                    }
                }
            } catch {}
            const orderIdKey = String(orderId);
            setOnlineOrders(prev => prev.map(o => String(o?.id ?? '') === orderIdKey ? (normalizedPaidOrder || o) : o));
            setHistory(prev => [savedHistoryItem, ...prev.filter(item => String(item?.id) !== String(savedHistoryItem.id))]);
            // #region debug-point register-payment-missing.client.addOnlineOrderPayment
            await reportRegisterPaymentDebugEvent({
                hypothesisId: 'H4',
                event: 'register_payment_state_applied',
                data: {
                    orderId,
                    historyItemId: savedHistoryItem?.id,
                    historyPaymentMethod: savedHistoryItem?.paymentMethod,
                    paymentStatus: normalizedPaidOrder?.paymentStatus,
                    paidAmount: normalizedPaidOrder?.paidAmount,
                    remainingAmount: normalizedPaidOrder?.remainingAmount
                }
            });
            // #endregion debug-point register-payment-missing.client.addOnlineOrderPayment
            return normalizedPaidOrder || data;
        } catch (e) {
            console.error(e);
            alert('Erro ao registrar pagamento.');
            // #region debug-point register-payment-missing.client.addOnlineOrderPayment
            await reportRegisterPaymentDebugEvent({
                hypothesisId: 'H2',
                event: 'register_payment_exception',
                data: {
                    orderId,
                    message: e?.message || String(e || '')
                }
            });
            // #endregion debug-point register-payment-missing.client.addOnlineOrderPayment
            return null;
        }
    };

    const emitFiscalDocument = async (saleData, type) => {
        try {
            // 1. PDF Retrieval (DANFE)
            if (type === 'DANFE') {
                const fiscalId = saleData.fiscalDoc?.id;
                // If fiscalDoc exists but type is missing, assume NFe (most common for DANFE button)
                const fiscalType = saleData.fiscalDoc?.type || 'NFe'; 
                
                if (!fiscalId) {
                    alert('Este pedido ainda não possui nota fiscal emitida. Emita a NFe primeiro.');
                    return null;
                }

                return {
                    id: fiscalId,
                    type: fiscalType,
                    status: saleData?.fiscalDoc?.status || saleData?.fiscalDocument?.status || '100'
                };
            }

            // 2. Emission (NFe or NFCe)
            if (saleData?.fiscalDoc?.id && isAuthorizedFiscalStatus(saleData?.fiscalDoc?.status)) {
                const fiscalType = saleData.fiscalDoc?.type || type || 'NFe';
                alert(`Nota já emitida para esta venda (Série ${saleData.fiscalDoc.serie} Nº ${saleData.fiscalDoc.number}).`);
                return {
                    id: saleData.fiscalDoc.id,
                    type: fiscalType,
                    numero: saleData.fiscalDoc.number,
                    serie: saleData.fiscalDoc.serie,
                    status: saleData.fiscalDoc.status || 'autorizada',
                    mensagem_sefaz: saleData.fiscalDoc.mensagem_sefaz,
                    motivo_status: saleData.fiscalDoc.motivo_status
                };
            }

            const txId = saleData?.id ?? saleData?._id ?? saleData?.orderId ?? saleData?.timestamp ?? (saleData?.date ? new Date(saleData.date).getTime() : null);
            const txKey = txId ? `pos_tx_${type}_${String(txId)}` : null;
            const existing = (typeof window !== 'undefined' && txKey) ? localStorage.getItem(txKey) : null;
            if (existing) {
                const prev = JSON.parse(existing);
                if (prev?.id && isAuthorizedFiscalStatus(prev?.status)) {
                    alert(`Nota já foi gerada para esta venda (Série ${prev.serie} Nº ${prev.numero}).`);
                    return prev;
                }
                localStorage.removeItem(txKey);
            }

            const endpoint = type === 'NFCe' ? '/api/nfce' : '/api/nfe';
            
            const rawCustomer = saleData?.cliente || saleData?.customer || saleData?.client || {};
            const rawAddress = rawCustomer?.address || saleData?.address || {};
            const normalizedCliente = {
                ...(saleData?.cliente && typeof saleData.cliente === 'object' ? saleData.cliente : {}),
                nome: saleData?.cliente?.nome || rawCustomer?.nome || rawCustomer?.name || saleData?.clientName || 'Cliente Diverso',
                cpf: String(saleData?.cliente?.cpf || rawCustomer?.cpf || '').replace(/\D/g, '') || undefined,
                cnpj: String(saleData?.cliente?.cnpj || rawCustomer?.cnpj || rawCustomer?.document || '').replace(/\D/g, '') || undefined,
                ie: saleData?.cliente?.ie ?? rawCustomer?.ie ?? undefined,
                logradouro: saleData?.cliente?.logradouro || rawAddress?.logradouro || rawAddress?.street || '',
                numero: saleData?.cliente?.numero || rawAddress?.numero || rawAddress?.number || '',
                bairro: saleData?.cliente?.bairro || rawAddress?.bairro || rawAddress?.neighborhood || '',
                cep: String(saleData?.cliente?.cep || rawAddress?.cep || '').replace(/\D/g, ''),
                cidade: saleData?.cliente?.cidade || rawAddress?.cidade || rawAddress?.city || '',
                uf: String(saleData?.cliente?.uf || rawAddress?.uf || rawAddress?.state || '').trim().toUpperCase(),
                cMun: String(saleData?.cliente?.cMun || rawAddress?.cMun || rawAddress?.ibge || '').replace(/\D/g, '') || undefined,
                complemento: saleData?.cliente?.complemento || rawAddress?.complemento || rawAddress?.complement || ''
            };

            // Prepare payload
            const payload = {
                ...saleData,
                fiscal: {
                    ...(saleData?.fiscal || {}),
                    manualIcmsRate: saleData?.fiscal?.manualIcmsRate ?? saleData?.fiscal?.aliquotaIcms ?? '',
                    manualIcmsValue: saleData?.fiscal?.manualIcmsValue ?? saleData?.fiscal?.valorIcms ?? '',
                    aliquotaIcms: saleData?.fiscal?.aliquotaIcms ?? saleData?.fiscal?.manualIcmsRate ?? '',
                    valorIcms: saleData?.fiscal?.valorIcms ?? saleData?.fiscal?.manualIcmsValue ?? '',
                    pICMS: saleData?.fiscal?.pICMS ?? saleData?.fiscal?.manualIcmsRate ?? '',
                    vICMS: saleData?.fiscal?.vICMS ?? saleData?.fiscal?.manualIcmsValue ?? ''
                },
                cliente: normalizedCliente,
                customer: {
                    ...(saleData?.customer || {}),
                    name: rawCustomer?.name || rawCustomer?.nome || normalizedCliente.nome,
                    cpf: normalizedCliente.cpf,
                    cnpj: normalizedCliente.cnpj,
                    ie: normalizedCliente.ie,
                    address: {
                        ...(saleData?.customer?.address || {}),
                        cep: normalizedCliente.cep,
                        street: normalizedCliente.logradouro,
                        number: normalizedCliente.numero,
                        neighborhood: normalizedCliente.bairro,
                        city: normalizedCliente.cidade,
                        state: normalizedCliente.uf,
                        cMun: normalizedCliente.cMun,
                        complement: normalizedCliente.complemento
                    }
                },
                client: {
                    ...(saleData?.client || {}),
                    name: rawCustomer?.name || rawCustomer?.nome || normalizedCliente.nome,
                    cpf: normalizedCliente.cpf,
                    cnpj: normalizedCliente.cnpj,
                    ie: normalizedCliente.ie,
                    address: {
                        ...(saleData?.client?.address || {}),
                        cep: normalizedCliente.cep,
                        street: normalizedCliente.logradouro,
                        number: normalizedCliente.numero,
                        neighborhood: normalizedCliente.bairro,
                        city: normalizedCliente.cidade,
                        state: normalizedCliente.uf,
                        cMun: normalizedCliente.cMun,
                        complement: normalizedCliente.complemento
                    }
                },
                createdBy: systemUser?.username || 'Sistema'
            };

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                const errorMsg = 
                    errorData.details?.error?.message || 
                    errorData.details?.message || 
                    errorData.error || 
                    (typeof errorData.details === 'string' ? errorData.details : 'Erro desconhecido na emissão');
                throw new Error(errorMsg);
            }

            const result = await response.json();
            
            // Se o servidor retornar o pedido atualizado, sincroniza no estado global
            if (result.order) {
                const normalizedResultOrder = normalizeOrder(result.order);
                setOnlineOrders(prev => prev.map(o => o.id === result.order.id ? (normalizedResultOrder || o) : o));
                setHistory(prev => prev.map(o => o.id === result.order.id ? result.order : o));
            }

            try {
                if (typeof window !== 'undefined' && txKey && result?.id && isAuthorizedFiscalStatus(result?.status)) {
                    localStorage.setItem(txKey, JSON.stringify(result));
                }
            } catch (e) {}
            
            // Return result so component can update its state (e.g., showing the new fiscalDoc ID)
            return result;

        } catch (error) {
            console.error(`Erro ao emitir ${type}:`, error);
            alert(`Erro ao emitir ${type}: ${error.message}`);
            return null;
        }
    };

    const loginClient = (clientData) => {
        setCurrentClient(clientData);
        localStorage.setItem('doutor_catalog_user', JSON.stringify(clientData));
        addClient(clientData);
    };

    const logoutClient = () => {
        setCurrentClient(null);
        localStorage.removeItem('doutor_catalog_user');
    };

    const loginSystem = async (username, password) => {
        try {
            const res = await fetch('/api/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });
            if (!res.ok) {
                return null;
            }
            const safeUser = normalizeSystemUserAccess(await res.json());
            setSystemUser(safeUser);
            loadedResourcesRef.current = { ...emptyLoadState };
            setLoadedResources({ ...emptyLoadState });
            setInitialDataLoaded(false);
            localStorage.setItem('doutor_system_user', JSON.stringify(safeUser));
            return safeUser;
        } catch (error) {
            console.error('Erro no login do sistema', error);
            return null;
        }
    };

    const logoutSystem = () => {
        setSystemUser(null);
        loadedResourcesRef.current = { ...emptyLoadState };
        setLoadedResources({ ...emptyLoadState });
        setInitialDataLoaded(false);
        localStorage.removeItem('doutor_system_user');
    };

    const updateSettings = async (newSettings) => {
        const normalizedSettings = ensureDuduSalesSeller(newSettings);
        setSettings(prev => ({ ...prev, ...normalizedSettings }));
        try {
            await fetch('/api/settings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(normalizedSettings)
            });
        } catch (e) {
            console.error('Erro ao salvar configurações no servidor:', e);
        }
    };

    const renumberProducts = async () => {
        try {
            const res = await fetch('/api/inventory/renumber', { method: 'POST' });
            if (res.ok) {
                const data = await res.json();
                console.log('Renumeração concluída:', data);
                
                // Refresh data from server
                const [invRes, histRes, ordRes] = await Promise.all([
                    fetch('/api/inventory?light=true'),
                    fetch('/api/history'),
                    fetch('/api/orders')
                ]);
                
                if (invRes.ok) {
                    const serverInventory = await invRes.json();
                    setInventory(serverInventory.map(normalizeProduct));
                }
                if (histRes.ok) {
                    const serverHistory = await histRes.json();
                    setHistory(serverHistory.map(normalizeHistoryItem));
                }
                if (ordRes.ok) {
                    const serverOrders = await ordRes.json();
                    setOnlineOrders(serverOrders.map(normalizeOrder).filter(o => o !== null));
                }
                
                return true;
            }
            return false;
        } catch (e) {
            console.error('Erro ao renumerar:', e);
            return false;
        }
    };

    const recalcRetailPrices = async () => {
        try {
            const res = await fetch('/api/inventory/recalc-retail-prices', { method: 'POST' });
            if (!res.ok) return { ok: false };
            const data = await res.json().catch(() => ({}));
            const invRes = await fetch('/api/inventory?light=true');
            if (invRes.ok) {
                const serverInventory = await invRes.json();
                setInventory(Array.isArray(serverInventory) ? serverInventory.map(normalizeProduct) : []);
            }
            return { ok: true, checked: data?.checked ?? null, updated: data?.updated ?? null };
        } catch (e) {
            console.error('Erro ao recalcular preços:', e);
            return { ok: false };
        }
    };

    return (
        <DataContext.Provider value={{
            systemUser, loginSystem, logoutSystem,
            inventory, history, suppliers, clients, onlineOrders, onlineOrdersSyncError, initialDataLoaded, settings, cart, currentClient,
            cacheOnlineOrder, orderBoardSummary, setOrderBoardSummary,
            setInventory, setHistory, setSuppliers, setClients, setOnlineOrders, setCart, setCurrentClient,
            addProduct, updateProduct, deleteProduct, 
            addHistory, updateHistoryItem, restoreBackup,
            addSale, addInteriorWithdrawal,
            addSupplier, updateSupplier, deleteSupplier,
            addClient, deleteClient, addOnlineOrder, updateOnlineOrderStatus, updateOnlineOrder, addOnlineOrderPayment, updateSettings,
            ensureRouteData, loadedResources,
            loginClient, logoutClient, emitFiscalDocument, renumberProducts, recalcRetailPrices
        }}>
            {children}
        </DataContext.Provider>
    );
}
